import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import { hasCallToAction, makeImagePreviewable } from '../utils';

import Block from './Block';
import H2 from './H2';
import RichTextHtml from './RichTextHtml';
import Cell from './Cell';
import Grid from './Grid';
import CTAAnchor from './CTAAnchor';

const getRandomInt = (min, max) =>
  Math.floor(Math.random() * (max - min)) + min;

const OverlappingImageStyles = css`
  width: ${({ numImages }) => (numImages > 1 ? '80%' : '100%')};

  ${({ index }) => {
    const isOdd = index % 2 !== 0;
    const width = getRandomInt(60, 80);
    const left = 100 - width;

    if (index === 0) {
      return 'left: 0;';
    }

    if (isOdd) {
      return `
        width: ${width}%;
        left: ${getRandomInt(left / 1.4, left)}%;
      `;
    }

    return `
      width: ${width}%;
      left: ${getRandomInt(0, left / 3)}%;
    `;
  }}

  margin-top: ${({ index }) => `-${index * 40}px;`};
`;

const TextImage = styled(GatsbyImage)`
  ${OverlappingImageStyles};
`;

const TextImg = styled.img`
  ${OverlappingImageStyles};
`;

const PageText = ({ content }) => {
  const hasImage =
    content.items &&
    content.items.length &&
    makeImagePreviewable(content.items[0].image).localFile;
  const isDarkTheme =
    content.primary.background_color === 'blueDarker' ||
    content.primary.background_color === 'blueDark';

  return (
    <Block
      id={content.primary.internal_link_id}
      backgroundColor={content.primary.background_color}
      py={{ mobile: 'baseUp4', tablet: 'baseUp12' }}
    >
      <Grid
        halign={hasImage ? 'left' : 'center'}
        flexDirection={
          content.primary.image_position === 'right' ? 'row-reverse' : 'row'
        }
      >
        {hasImage ? (
          <Cell size={{ mobile: 1, tablet: 1 / 2 }}>
            <Block px="baseUnit" py={{ mobile: 'baseDown2', tablet: '0' }}>
              {content.items.map(({ image }, i) => {
                const previewableImage = makeImagePreviewable(image);
                const processedByChildImageSharp = Boolean(
                  previewableImage.localFile &&
                    previewableImage.localFile.childImageSharp,
                );

                return processedByChildImageSharp ? (
                  <TextImage
                    key={`${previewableImage.url}-${i}`}
                    image={
                      previewableImage.localFile.childImageSharp.gatsbyImageData
                    }
                    alt={previewableImage.alt || ''}
                    index={i}
                    numImages={content.items.length}
                  />
                ) : (
                  <TextImg
                    key={`${previewableImage.url}-${i}`}
                    src={previewableImage.localFile.url}
                    alt={previewableImage.alt}
                    index={i}
                    numImages={content.items.length}
                  />
                );
              })}
            </Block>
          </Cell>
        ) : null}
        <Cell size={{ mobile: 1, tablet: hasImage ? 1 / 2 : 1 / 1.5 }}>
          <Block
            px="baseUnit"
            py={{ mobile: 'baseDown2', tablet: '0' }}
            pl="baseUp4"
          >
            <H2 mb="baseUnit" color={isDarkTheme ? 'coralLight' : 'blueDark'}>
              {content.primary.title1.text}
            </H2>

            <RichTextHtml
              html={content.primary.text.html}
              color={isDarkTheme ? 'white' : 'black'}
            />

            {hasCallToAction(content.primary) ? (
              <Block pt="baseUp4">
                <CTAAnchor
                  text={content.primary.call_to_action_text.text}
                  link={content.primary.call_to_action_link}
                  colorTheme={content.primary.call_to_action_color}
                />
              </Block>
            ) : null}
          </Block>
        </Cell>
      </Grid>
    </Block>
  );
};

PageText.propTypes = {
  content: PropTypes.shape({
    primary: PropTypes.shape({
      internal_link_id: PropTypes.string,
      title1: PropTypes.shape({ text: PropTypes.string }),
      text: PropTypes.shape({ html: PropTypes.string }),
      image_position: PropTypes.string,
      background_color: PropTypes.string,
      call_to_action_color: PropTypes.string,
      call_to_action_text: PropTypes.shape({ text: PropTypes.string }),
      call_to_action_link: PropTypes.shape({
        url: PropTypes.string,
        link_type: PropTypes.string,
        lang: PropTypes.string,
        target: PropTypes.string,
      }),
    }),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.shape({
          alt: PropTypes.string,
          localFile: PropTypes.shape({}),
        }),
      }),
    ),
  }).isRequired,
};

export default PageText;

export const query = graphql`
  fragment PageTextFragment on PrismicPageDataBodyText {
    id
    slice_type
    primary {
      internal_link_id
      title1 {
        text
      }
      text {
        html
      }
      image_position
      background_color
      call_to_action_color
      call_to_action_text {
        text
      }
      call_to_action_link {
        link_type
        url
        lang
        target
      }
    }
    items {
      image {
        alt
        localFile {
          url
          childImageSharp {
            gatsbyImageData(width: 550)
          }
        }
      }
    }
  }
`;
